import { useGetAllDeviceParameterQuery } from "@/redux/api/deviceParameterApi";
import { setDateRange, setGroupData, setPrevTabId, setRefetch, setTimer, popFromHistory } from "@/redux/slices/dashboardV2.slice";
import { Button, Checkbox, DatePicker, Dropdown, Input, Select } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { BiRefresh, BiSearch, BiX, BiZoomOut } from "react-icons/bi";
import { FiClock } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomSelectorContainer from "./CustomSelectorContainer";
import { IoReturnUpBack } from "react-icons/io5";
import { multipleAvail } from "@/constant/globalConstant";

export const TimePeriodFilter = ({ customSelectorProps }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ref = useRef();
  const timeInputRef = useRef();
  const { dateRange, prevTabId, prevDashboardHistory } = useSelector((state) => state.dashboardV2) || {};
  const { timer } = useSelector((state) => state.dashboardV2);
  const { isFullScreen } = useSelector((state) => state.theme);
  const { groupData } = useSelector((state) => state.dashboardV2);
  const refreshIntervalRef = useRef(null);

  const { dashboard, tabId, setData = () => {} } = customSelectorProps;

  // states
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [peak, setPeak] = useState(false);
  const [timeOptions, setTimeOptions] = useState(quickTimeOption);
  const [activeTab, setActiveTab] = useState({});

  // rkt query
  const { data: parameterData, isFetching: isParameterListFetching } = useGetAllDeviceParameterQuery(
    {
      business: customSelectorProps?.dashboard?.business,
      limit: 1000,
      page: 1,
      sortBy: "name",
      orderBy: "ASC",
      type: "formula",
      populate: "device device.site",
    },
    { skip: !customSelectorProps?.dashboard?.business },
  );
  const parameterList = parameterData?.data || [];

  useEffect(() => {
    const activeTab = dashboard.hasTabs ? dashboard?.tabs?.find((tab) => tab._id === tabId) : dashboard?.tabs?.[0];
    setActiveTab(activeTab);
  }, [dashboard, tabId]);

  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (timer && parseInt(timer) > 0) {
      refreshIntervalRef.current = setInterval(
        () => {
          handleRefresh();
        },
        parseInt(timer) * 1000,
      );
    }

    return () => {
      clearInterval(refreshIntervalRef.current);
    };
  }, [timer, isFullScreen]);

  //  sync with redux
  useEffect(() => {
    if (dateRange.type === "absolute") {
      setStartDate(dateRange?.startDate ? dayjs(dateRange?.startDate) : undefined);
      setEndDate(dateRange?.endDate ? dayjs(dateRange?.endDate) : undefined);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
    setPeak(dateRange?.peakHours);
  }, [dateRange]);

  const currentSelectTime = () => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const { startDate, endDate } = dateRange;
      const withoutTimeStartDate = dayjs(startDate).format("YYYY/MM/DD");
      const withoutTimeEndDate = dayjs(endDate).format("YYYY/MM/DD");
      if (dateRange?.peakHours) {
        return `${withoutTimeStartDate} - ${withoutTimeEndDate} ${dateRange?.peakHours === "peak" ? `(Peak Hours)` : "(Off-Peak Hours)"}  `;
      }
      return `${dayjs(startDate).format("YYYY/MM/DD : HH:mm")} - ${dayjs(endDate).format("YYYY/MM/DD : HH:mm")}`;
    } else {
      return "Select Time";
    }
  };

  // handler
  const onChangePeakHour = (e) => {
    const { value, checked } = e.target;
    setPeak(checked ? value : null);
  };

  const handleClear = () => {
    dispatch(setDateRange({}));
    dispatch(setTimer(0));
    setStartDate();
    setEndDate();
  };

  const onSearchTimeOption = (e) => {
    const search = quickTimeOption.filter((tm) => tm.label.includes(e.target.value));
    setTimeOptions(search);
  };

  // on handle date range
  const onApply = () => {
    if (startDate && endDate) {
      dispatch(
        setDateRange({
          startDate: startDate?.startOf("day")?.toISOString(),
          endDate: endDate?.endOf("day")?.toISOString(),
          type: "absolute",
          peakHours: peak,
        }),
      );
    }
    setOpen(false);
  };
  // handle quick range selector
  const onSelectQuickTimer = ({ durationType, value }) => {
    const startDate = dayjs().subtract(value, durationType).toISOString();
    const endDate = dayjs().toISOString();

    dispatch(setDateRange({ startDate, endDate, type: "quick", durationType }));
    setOpen(false);
  };

  const handleZoomOut = () => {
    const startDate = dayjs(dateRange?.startDate).subtract(1, dateRange?.durationType).toISOString();
    dispatch(setDateRange({ ...dateRange, startDate }));
  };

  const handleRefresh = () => {
    dispatch(setRefetch(new Date().toISOString()));
  };

  const handleGroupChange = (value, ids, index) => {
    const name = `group${index + 1}`;
    if (groupData.length <= 0) {
      dispatch(setGroupData([{ name, parameters: value, ids }]));
    } else {
      const updated = groupData.map((group) => (group?.name === name ? { ...group, parameters: value, ids } : { parameters: value, ids, name }));
      dispatch(setGroupData(updated));
    }
  };

  const handleBackLinkedTab = (tabId) => {
    if (tabId) {
      setData((pre) => {
        const prevTabs = pre?.tabs?.map((item) => ({ ...item, isSelected: item.tabId === tabId }));
        return { ...pre, tabs: prevTabs };
      });
      dispatch(setPrevTabId(null));
    }
  };

  useEffect(() => {
    dispatch(setGroupData([]));
  }, [activeTab?.groups]);

  return (
    // live edit
    <div className="d-flex ms-auto flex-wrap align-items-center gap-2">
      {activeTab?.liveEdit ? (
        <>
          {Array.isArray(activeTab?.groups)
            ? activeTab?.groups?.map((item, idx) => {
                const value = groupData?.find((group) => group.name === `group${idx + 1}`)?.parameters || [];
                const isSingle = isSingleFn(item?.values || [], activeTab.items || []);
                return (
                  <div className="d-flex align-items-center gap-2" key={idx}>
                    <div
                      style={{
                        background: "var(--dark-color)",
                        color: "var(--color-black)",
                        padding: "5px 10px",
                        borderRadius: "5px",
                      }}
                      type="primary"
                      className=""
                    >
                      {item?.name || `Group ${idx + 1}`}
                    </div>

                    <Select
                      style={{ width: 180 }}
                      className="home-dropdown"
                      placeholder={"Select Parameters " + (idx + 1)}
                      variant="filled"
                      loading={isParameterListFetching}
                      onChange={(val) => {
                        if (val.includes("all")) {
                          if (value.length === 0) {
                            handleGroupChange(
                              parameterList.map((p) => p._id),
                              item?.values,
                              idx,
                            );
                          } else {
                            handleGroupChange([], item?.values, idx);
                          }
                        } else {
                          handleGroupChange(val, item?.values, idx);
                        }
                      }}
                      maxTagCount="responsive"
                      maxTagTextLength={7}
                      value={value}
                      allowClear
                      showSearch={{
                        filterOption: (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase()),
                      }}
                      mode={isSingle ? undefined : "multiple"}
                      popupMatchSelectWidth={false}
                      options={[
                        ...(isSingle
                          ? []
                          : [
                              {
                                label: value.length === 0 ? "Select All" : "Remove All",
                                value: "all",
                              },
                            ]),
                        ...(Array.isArray(parameterList)
                          ? parameterList.map((param) => ({
                              label: `${param?.device?.site?.name ? param?.device?.site?.name + " > " : ""}${param?.device?.name ? param?.device?.name + " > " : ""} ${param?.name}`,
                              value: param._id,
                            }))
                          : []),
                      ]}
                    />
                  </div>
                );
              })
            : ""}
        </>
      ) : (
        ""
      )}

      {prevTabId ? (
        <Button
          onClick={() => handleBackLinkedTab(prevTabId)}
          className="bg_light_gray"
          htmlType="button"
          type="primary"
          title="Back to Previous Tab"
        >
          <IoReturnUpBack />
        </Button>
      ) : (
        ""
      )}

      <CustomSelectorContainer {...customSelectorProps} />

      <div ref={ref} className="d-flex bg_light_gray overflow-hidden rounded-2" style={{ padding: 3 }}>
        <Dropdown
          placement="topLeft"
          classNames={{ root: "time-dropdown" }}
          open={open}
          onOpenChange={setOpen}
          getPopupContainer={(triggerNode) => triggerNode}
          menu={{
            items: [
              {
                key: "0",
                disabled: true,
                label: (
                  <div
                    style={{
                      width: "500px",
                    }}
                  >
                    <div className="d-flex justify-content-end mb-3" style={{ fontSize: "18px" }}>
                      <Modal.Header className="text-right" closeButton onClick={() => setOpen(false)} />
                    </div>
                    <Row>
                      <Col xs={12} md={7} className="text-start">
                        <h6 className="mb-3">Absolute Time Range</h6>
                        <div className="mb-2 time-input">
                          <label className="fw-bold">From</label>
                          <div ref={timeInputRef}>
                            <DatePicker
                              onChange={(value) => setStartDate(value)}
                              value={startDate ? startDate : ""}
                              disabledDate={(current) => current && current.valueOf() > new Date()}
                              size="large"
                              className="w-100"
                              getPopupContainer={(triggerNode) => triggerNode}
                              placement="topLeft"
                            />
                          </div>
                        </div>
                        <div className="time-input">
                          <label className="fw-bold">To</label>
                          <div ref={timeInputRef}>
                            <DatePicker
                              disabled={!startDate}
                              value={endDate ? endDate : ""}
                              onChange={(value) => {
                                setEndDate(value);
                              }}
                              disabledDate={(current) => current && (current.valueOf() < startDate || current.valueOf() > new Date())}
                              getPopupContainer={(triggerNode) => triggerNode}
                              size="large"
                              className="w-100"
                              placement="topLeft"
                            />
                          </div>
                        </div>
                        {/* {console.log(peakHours)} */}
                        <div className="mt-3">
                          <Checkbox value={"peak"} checked={peak === "peak"} onChange={onChangePeakHour}>
                            Peak Hours (8.00am - 10.00pm)
                          </Checkbox>
                        </div>
                        <div className="mt-1 text-wrap">
                          <Checkbox value={"non_peak"} checked={peak === "non_peak"} onChange={onChangePeakHour}>
                            Off-Peak Hours (12.00am - 8.00am / 10.00pm - 12.00pm)
                          </Checkbox>
                        </div>
                        <div className="mt-4 text-end">
                          <Button disabled={!startDate || !endDate} onClick={onApply} type="primary">
                            Apply
                          </Button>
                        </div>
                      </Col>
                      <Col xs={12} md={5} style={{ borderLeft: "1px solid var(--color_light)" }}>
                        <div className="time-input">
                          <Input
                            size="middle"
                            placeholder="Search quick ranges"
                            prefix={<BiSearch />}
                            onChange={onSearchTimeOption}
                            className="w-100"
                          />
                        </div>
                        <div className="my-3 quick-time-option">
                          {timeOptions.map((time) => (
                            <Button
                              type="text"
                              // danger
                              style={{ display: "block", fontWeight: "500", cursor: "pointer" }}
                              onClick={() => onSelectQuickTimer(time)}
                              key={time?.label}
                            >
                              {time?.label}
                            </Button>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </div>
                ),
              },
            ],
          }}
          trigger={["click"]}
        >
          <button
            className="d-flex align-items-center bg_light_gray"
            style={{
              padding: "3px 10px",
              border: "1px solid var(--color_light)",
              fontSize: "14px",
            }}
            onClick={() => setOpen(!open)}
          >
            <FiClock className="mr-2" fontSize="14px" />
            <span className="mr-2">{currentSelectTime()}</span>
          </button>
        </Dropdown>
        <button className="bg-transparent" style={{ border: "none" }} onClick={handleZoomOut}>
          <BiZoomOut fontSize="18px" />
        </button>
      </div>

      {(dateRange?.startDate || dateRange?.endDate) && (
        <div
          className="mr-2"
          style={{
            background: "red",
            fontSize: "19px",
            color: "#fff",
            padding: "0px 5px",
            borderRadius: "5px",
            cursor: "pointer",
          }}
          onClick={handleClear}
        >
          <BiX style={{ marginBottom: "4px" }} />
        </div>
      )}

      <Button type="primary" htmlType="button" className="bg_light_gray" onClick={handleRefresh}>
        <BiRefresh fontSize="20px" />
      </Button>

      <Select
        style={{
          width: 80,
        }}
        className="home-dropdown"
        variant="filled"
        onChange={(value) => dispatch(setTimer(value))}
        value={timer || ""}
        options={timerItems}
      />
    </div>
  );
};

export default TimePeriodFilter;

TimePeriodFilter.propTypes = {
  customSelectorProps: PropTypes.object,
};

const quickTimeOption = [
  { label: "Last 5 minutes", value: 5, durationType: "minute" },
  { label: "Last 15 minutes", value: 15, durationType: "minute" },
  { label: "Last 30 minutes", value: 30, durationType: "minute" },
  { label: "Last 1 hours", value: 1, durationType: "hour" },
  { label: "Last 3 hours", value: 3, durationType: "hour" },
  { label: "Last 6 hours", value: 6, durationType: "hour" },
  { label: "Last 12 hours", value: 12, durationType: "hour" },
  { label: "Last 24 hours", value: 24, durationType: "hour" },
  { label: "Last 2 days", value: 2, durationType: "day" },
  { label: "Last 3 days", value: 3, durationType: "day" },
  { label: "Last 7 days", value: 7, durationType: "day" },
  { label: "Last 30 days", value: 30, durationType: "day" },
  { label: "Last 3 months", value: 3, durationType: "month" },
  { label: "Last 6 months", value: 6, durationType: "month" },
  { label: "Last 12 months", value: 12, durationType: "month" },
];

const timerItems = [
  {
    value: "",
    label: "Off",
  },
  {
    label: "5s",
    value: "5",
  },
  {
    label: "10s",
    value: "10",
  },
  {
    label: "30s",
    value: "30",
  },
  {
    label: "1m",
    value: "60",
  },
  {
    label: "5m",
    value: "300",
  },
  {
    label: "30m",
    value: "1800",
  },
  {
    label: "1h",
    value: "3600",
  },
  {
    label: "2h",
    value: "7200",
  },
  {
    label: "1d",
    value: "86400",
  },
];
const isSingleFn = (ids, items) => {
  return ids.some((id) => {
    const item = items.find((i) => String(i._id) === String(id));
    return !multipleAvail.includes(item?.component);
  });
};
